/**
 * "Where is this question's work?" — correcting the region one question was
 * analysed from, after the fact.
 *
 * The splitter decides which stretch of an uploaded script answers which
 * question, and when it gets that wrong the analyser grades the wrong evidence
 * faithfully: on the attempt this was built for, a student was told they had
 * misread a question because the NEXT question's working had been filed under
 * theirs. This is how a person says where it actually is.
 *
 * One question at a time, on purpose. Nothing here touches any other question's
 * image, mapping or result — a paper with two mis-attributed questions takes two
 * corrections, each one somebody's deliberate act.
 *
 * The crop is previewed from the page's own pixels before anything is sent. The
 * model's boxes are not cropped to precisely because they clip; a human box is
 * honoured literally, and seeing the crop first is what makes that safe.
 *
 * See docs/FeatureDesigns/assignments-tests/solution-region-correction-target.md.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { AdjustmentWhyFields, adjustmentWhyPayload, adjustmentWhyProblem, Button, Modal, type AdjustmentWhyValue } from '../components';
import { LatexText } from '../latex';
import type { ResolvedUpload, UploadQuestionRegions } from '../session-results';
import './RegionCorrector.css';

/** `[ymin, xmin, ymax, xmax]`, integers 0–1000 normalized to the page — the same
 *  value the splitter emits, so a drawn box and a detected one are one type. */
export type Box2d = [number, number, number, number];

export interface CorrectableQuestion {
  questionId: string;
  /** Position in the paper — what the student wrote next to their working.
   *  Absent when the caller arrived without the attempt's ordering. */
  questionIndex?: number;
  /** The number as printed on the paper ('01.', 'Q7'). Preferred over
   *  `questionIndex` when both are known — it is what the student wrote. */
  printedNumber?: string;
  questionText: string;
}

/** 'Q7' — from the printed number when there is one, else the position. */
function questionLabel(q: CorrectableQuestion): string | null {
  const printed = q.printedNumber?.trim().replace(/[.):]+$/, '');
  if (printed) return /^\d/.test(printed) ? `Q${printed}` : printed;
  return q.questionIndex != null ? `Q${q.questionIndex + 1}` : null;
}

/** The stem alone, for a one-line heading: inline images go, and so does
 *  everything from a blank line or an option-style line ('(a) ', '1) ') on. */
function stemOneLine(text: string): string {
  return text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .split(/\n\s*\n|\n(?=\s*\(?[a-dA-D1-4][).]\s)/)[0]
    .replace(/\s+/g, ' ')
    .trim();
}

export interface RegionCorrectionRequest {
  questionId: string;
  regions: { pageIndex: number; box2d: Box2d }[];
  reason?: string;
  uploadId?: string;
  /** Whose side, what exactly, a note for our records — teachers and admins. */
  origin?: AdjustmentWhyValue['origin'];
  reasonCode?: AdjustmentWhyValue['reasonCode'];
  internalComment?: string;
}

export interface RegionCorrectionResponse {
  correctionId: string;
  imageUrl: string;
  rerun?: { newSubmissionId?: string; previousSubmissionId?: string; clearedEncounters: number };
}

export interface RegionCorrectorProps {
  questions: CorrectableQuestion[];
  /** Which question to open on — the one the reader thinks is wrong. */
  initialQuestionId?: string;
  /** The archived pages + what each question's region currently is. */
  resolveUpload: () => Promise<ResolvedUpload>;
  correctRegion: (input: RegionCorrectionRequest) => Promise<RegionCorrectionResponse>;
  onClose: () => void;
  /** Fired after a correction lands, so the caller can refresh the result it is
   *  showing — the score behind it has just changed. */
  onCorrected?: (result: RegionCorrectionResponse) => void;
  /** Ask a teacher or admin whose side the problem was on (required), what
   *  exactly, and for a note for our records. Not asked of a student correcting
   *  their own paper. See analysis-adjustments-target.md §4e. */
  askWhy?: boolean;
  /** "No working for this question": the area filed here is another
   *  question's, and the student wrote nothing for this one. Sets the working
   *  aside on `initialQuestionId` without a re-read — no model call, the mark
   *  stays (POST solution-analyses/{id}/attribution). Teachers and admins
   *  only. `markFromWorking` — the mark was read from that working, so it is
   *  said that it stands until a re-mark. */
  noWorking?: {
    submit: (why: AdjustmentWhyValue) => Promise<unknown>;
    markFromWorking?: boolean;
  };
}

/** ReactCrop hands back percentages of the displayed image; the record wants
 *  thousandths of the page. Same space, so no pixel or resolution math. */
function cropToBox2d(crop: Crop): Box2d {
  const round = (n: number) => Math.round(Math.min(1000, Math.max(0, n * 10)));
  return [round(crop.y), round(crop.x), round(crop.y + crop.height), round(crop.x + crop.width)];
}

/** Draw one region out of a loaded page at full resolution — the same crop the
 *  server will make, so what the corrector approves is what the analyser reads. */
function cropToDataUrl(img: HTMLImageElement, box2d: Box2d): string {
  const [ymin, xmin, ymax, xmax] = box2d;
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const sx = Math.round((xmin / 1000) * w);
  const sy = Math.round((ymin / 1000) * h);
  const sw = Math.max(1, Math.round(((xmax - xmin) / 1000) * w));
  const sh = Math.max(1, Math.round(((ymax - ymin) / 1000) * h));
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  canvas.getContext('2d')?.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  return canvas.toDataURL('image/jpeg', 0.9);
}

export function RegionCorrector({
  questions, initialQuestionId, resolveUpload, correctRegion, onClose, onCorrected, askWhy, noWorking,
}: RegionCorrectorProps) {
  const [upload, setUpload] = useState<ResolvedUpload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [questionId, setQuestionId] = useState(initialQuestionId ?? questions[0]?.questionId ?? '');
  const [pageIndex, setPageIndex] = useState(0);
  const [crop, setCrop] = useState<Crop>();
  const [regions, setRegions] = useState<{ pageIndex: number; box2d: Box2d; preview: string }[]>([]);
  const [reason, setReason] = useState('');
  const [why, setWhy] = useState<AdjustmentWhyValue>({});
  const whyProblem = askWhy ? adjustmentWhyProblem(why, { origin: true }) : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmNoWorking, setConfirmNoWorking] = useState(false);

  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    let live = true;
    resolveUpload()
      .then(resolved => { if (live) setUpload(resolved); })
      .catch(err => { if (live) setLoadError(err instanceof Error ? err.message : 'Could not load the uploaded pages'); });
    return () => { live = false; };
  }, [resolveUpload]);

  const question = questions.find(q => q.questionId === questionId);
  const pages = upload?.pages ?? [];

  /** What we currently believe this question's work is — the split's reading, or
   *  an earlier correction. Shown so the corrector can see what they are
   *  disagreeing with rather than guessing. */
  const existing: UploadQuestionRegions | undefined = useMemo(
    () => upload?.questionRegions?.find(r => r.questionId === questionId),
    [upload, questionId],
  );

  // Open on a page this question is already associated with: the mistake is
  // nearly always next to where we looked, not on an unrelated page.
  useEffect(() => {
    const first = existing?.regions[0]?.pageIndex;
    if (first != null) setPageIndex(first);
    setRegions([]);
    setCrop(undefined);
  }, [questionId, existing]);

  const addRegion = () => {
    if (!crop || crop.width < 1 || crop.height < 1 || !imgRef.current) {
      setError('Drag a box around the working first');
      return;
    }
    const box2d = cropToBox2d(crop);
    setRegions(prev => [...prev, { pageIndex, box2d, preview: cropToDataUrl(imgRef.current!, box2d) }]);
    setCrop(undefined);
    setError(null);
  };

  const submit = async () => {
    if (regions.length === 0) {
      setError('Add the area holding this question’s work');
      return;
    }
    if (whyProblem) {
      setError(whyProblem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await correctRegion({
        questionId,
        regions: regions.map(({ pageIndex: p, box2d }) => ({ pageIndex: p, box2d })),
        reason: reason.trim() || undefined,
        uploadId: upload?.uploadId,
        ...(askWhy ? adjustmentWhyPayload(why) : {}),
      });
      onCorrected?.(result);
      onClose();
    } catch (err) {
      // The correction and its crop are durable before the re-read runs, so a
      // failure here has not cost them the box they drew — say so, rather than
      // leaving them to redraw it.
      setError(err instanceof Error ? err.message : 'Could not correct this question');
    } finally {
      setBusy(false);
    }
  };

  // Only for the question the caller can settle — the one it opened on.
  const canSayNoWorking = !!noWorking && questionId === (initialQuestionId ?? questions[0]?.questionId);
  const submitNoWorking = async () => {
    setBusy(true);
    setError(null);
    try {
      await noWorking!.submit(askWhy ? adjustmentWhyPayload(why) : {});
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set the working aside');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Where is this question's work?" size="xl">
      <div className="rc-body">
        {loadError && <div className="rc-error">{loadError}</div>}
        {!upload && !loadError && <div className="rc-loading">Loading the uploaded pages…</div>}

        {upload && (
          <>
            <div className="rc-question">
              {/* A picker only when there is something to pick. Opened on one
                  question, the heading just says which. */}
              {questions.length > 1 && (
                <>
                  <label htmlFor="rc-question-select">Question</label>
                  <select
                    id="rc-question-select"
                    value={questionId}
                    onChange={e => setQuestionId(e.target.value)}
                    disabled={busy}
                  >
                    {questions.map((q, i) => (
                      <option key={q.questionId} value={q.questionId}>
                        {questionLabel(q) ?? `Question ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {question && (
                <div className="rc-question-line" title={stemOneLine(question.questionText)}>
                  {questionLabel(question) && <strong className="rc-question-num">{questionLabel(question)}</strong>}
                  <span className="rc-question-stem"><LatexText text={stemOneLine(question.questionText)} /></span>
                </div>
              )}
              <div className="rc-existing">
                {existing
                  ? existing.source === 'correction'
                    ? `Already corrected — page ${existing.regions[0].pageIndex + 1}`
                    : `We read this as page ${existing.regions.map(r => r.pageIndex + 1).join(', ')}`
                  : 'We found no work for this question'}
              </div>
            </div>

            <div className="rc-pager">
              <Button variant="secondary" disabled={pageIndex === 0} onClick={() => setPageIndex(p => p - 1)}>‹ Page</Button>
              <span>Page {pageIndex + 1} of {pages.length}</span>
              <Button variant="secondary" disabled={pageIndex >= pages.length - 1} onClick={() => setPageIndex(p => p + 1)}>Page ›</Button>
            </div>

            <div className="rc-page">
              {pages[pageIndex] && (
                <ReactCrop crop={crop} onChange={(_, percentCrop) => setCrop(percentCrop)} disabled={busy}>
                  <img
                    ref={imgRef}
                    src={pages[pageIndex].url}
                    alt={pages[pageIndex].label}
                    crossOrigin="anonymous"
                  />
                </ReactCrop>
              )}
            </div>

            <div className="rc-actions">
              <Button variant="secondary" onClick={addRegion} disabled={busy}>Use this area</Button>
              {canSayNoWorking && (
                <Button variant="secondary" onClick={() => setConfirmNoWorking(true)} disabled={busy || confirmNoWorking}
                  data-user-action="region_corrector_no_working">
                  No working for this question
                </Button>
              )}
              <span className="rc-hint">Add another area if the working runs onto a second page.</span>
            </div>

            {confirmNoWorking && (
              <div className="rc-no-working" role="group" aria-label="No working for this question">
                <p>
                  The steps and diagnosis are hidden; {noWorking!.markFromWorking
                    ? 'the mark stays, though it was read from this working — to re-mark, draw the right area instead'
                    : 'the mark stays'}; no AI re-check.
                </p>
                <Button onClick={submitNoWorking} disabled={busy}>{busy ? 'Saving…' : 'Set the working aside'}</Button>
                <Button variant="secondary" onClick={() => setConfirmNoWorking(false)} disabled={busy}>Back</Button>
              </div>
            )}

            {regions.length > 0 && (
              <div className="rc-preview">
                <div className="rc-preview-title">This is what will be marked</div>
                <div className="rc-preview-strip">
                  {regions.map((r, i) => (
                    <figure key={i}>
                      <img src={r.preview} alt={`Selected area ${i + 1}`} />
                      <figcaption>
                        Page {r.pageIndex + 1}
                        <button type="button" onClick={() => setRegions(prev => prev.filter((_, j) => j !== i))} disabled={busy}>
                          Remove
                        </button>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}

            {askWhy && (
              <AdjustmentWhyFields value={why} onChange={setWhy} originRequired disabled={busy} />
            )}

            <label className="rc-reason">
              {/* Sent to the AI as the re-evaluation note — so, once there is a
                  separate note for our records, it says who it is for. */}
              {askWhy ? 'Tell the AI what to look for (optional)' : 'What went wrong? (optional)'}
              <input
                type="text"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. this is Q11’s working, not Q10’s"
                disabled={busy}
              />
            </label>

            {error && <div className="rc-error">{error}</div>}

            <div className="rc-submit">
              <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy || regions.length === 0 || !!whyProblem} title={whyProblem ?? undefined}>
                {busy ? 'Re-marking…' : 'Correct and re-mark'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
